<template>
  <div
    v-if="show && filteredEmojis.length"
    ref="emojiDropdown"
    class="s-emojiDropdown"
    :style="{ top: position.top, left: position.left }">
    <button
      v-for="(emoji, index) in filteredEmojis"
      :key="emoji.id"
      class="s-emojiDropdown__item"
      :class="{ selected: index === selectedIndex }"
      @focus="selectedIndex = index"
      @mouseover="selectedIndex = index"
      @click="selectEmoji()">
      <span>
        <template v-if="emoji.skins && emoji.skins[0] && emoji.skins[0].src">
          <img
            :src="emoji.skins[0].src"
            alt=""
            width="20"
            height="20"
            style="vertical-align: middle;" />
        </template>
        <template v-else>
          {{ emoji.skins[0].native }}
        </template>
      </span>
      <span class="s-emojiDropdown__name">:{{ emoji.id }}:</span>
    </button>
  </div>
</template>

<script setup>
import {
  ref, watch, computed, onMounted, onBeforeUnmount, nextTick,
} from 'vue'

import { loadEmojiData } from '@/logic/emoji/emojiLoader'

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  query: {
    type: String,
    default: '',
  },
  position: {
    type: Object,
    default: () => ({ top: '0px', left: '0px' }),
  },
})

const emit = defineEmits(['select'])

const emojiDropdown = ref(null)
const selectedIndex = ref(0)

let emojiData = null

const getEmoticonCandidates = (query) => [
  `:${query}`,
  `:-${query}`,
]

const hasEmoticonMatch = (emoji, candidates) => {
  return Array.isArray(emoji.emoticons)
    && emoji.emoticons.some((emo) => {
      const v = (emo || '').toLowerCase()
      return candidates.includes(v)
    })
}

const filteredEmojis = computed(() => {
  if (!props.query) {
    return []
  }
  if (!emojiData?.emojis) {
    return []
  }

  const searchQuery = props.query.toLowerCase()
  const normalizedQuery = searchQuery.replace(/^:+|:+$/g, '')
  const shortcode = `:${normalizedQuery}:`
  const emoticonCandidates = getEmoticonCandidates(normalizedQuery)

  return Object.values(emojiData.emojis)
    .filter((emoji) => {
      const id = (emoji.id || '').toLowerCase()
      const name = (emoji.name || '').toLowerCase()
      const keywords = Array.isArray(emoji.keywords) ? emoji.keywords : []
      const idMatch = id.includes(searchQuery)
      const nameMatch = name.includes(searchQuery)
      const keywordMatch = keywords.some((k) => (k || '').toLowerCase().includes(searchQuery))
      const keywordWithColonsMatch = keywords.some((k) => (k || '').toLowerCase().includes(shortcode))
      const emoticonMatch = hasEmoticonMatch(emoji, emoticonCandidates)

      return idMatch || nameMatch || keywordMatch || keywordWithColonsMatch || emoticonMatch
    })
    .sort((a, b) => {
      const aEmo = hasEmoticonMatch(a, emoticonCandidates)
      const bEmo = hasEmoticonMatch(b, emoticonCandidates)
      if (aEmo !== bEmo) {
        if (aEmo) {
          return -1
        }
        return 1
      }

      // Secondary tie-breaker: id startsWith query
      const aId = (a.id || '').toLowerCase()
      const bId = (b.id || '').toLowerCase()
      const aStarts = aId.startsWith(searchQuery)
      const bStarts = bId.startsWith(searchQuery)
      if (aStarts !== bStarts) {
        if (aStarts) {
          return -1
        }
        return 1
      }

      // Otherwise keep relative order
      if (aId === bId) {
        return 0
      }
      return aId < bId ? -1 : 1
    })
})

const scrollToSelected = (direction) => {
  nextTick(() => {
    const dropdown = emojiDropdown.value
    const selectedItem = dropdown?.querySelectorAll('.s-emojiDropdown__item')[selectedIndex.value]

    if (selectedItem) {
      const dropdownRect = dropdown.getBoundingClientRect()
      const selectedRect = selectedItem.getBoundingClientRect()

      if (direction === 'down') {
        if (selectedRect.bottom > dropdownRect.bottom) {
          dropdown.scrollTop += selectedRect.bottom - dropdownRect.bottom
        }
      } else if (direction === 'up') {
        if (selectedRect.top < dropdownRect.top) {
          dropdown.scrollTop -= dropdownRect.top - selectedRect.top
        }
      }
    }
  })
}

const selectEmoji = (skinIndex = 0) => {
  const selectedEmoji = filteredEmojis.value[selectedIndex.value]
  if (!selectedEmoji || !selectedEmoji.skins || !selectedEmoji.skins.length) {
    return
  }

  const skin = selectedEmoji.skins[skinIndex]
  // Emit rich payload so caller can insert unicode or image
  if (skin.native) {
    emit('select', { type: 'native', value: skin.native, id: selectedEmoji.id })
  } else if (skin.src) {
    emit('select', { type: 'image', value: skin.src, id: selectedEmoji.id })
  } else {
    emit('select', { type: 'shortcode', value: `:${selectedEmoji.id}:`, id: selectedEmoji.id })
  }
}

const handleKeyDown = (event) => {
  if (!props.show) {
    return
  }
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    if (selectedIndex.value === filteredEmojis.value.length - 1) {
      return
    }
    selectedIndex.value = (selectedIndex.value + 1) % filteredEmojis.value.length
    scrollToSelected('down')
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    if (selectedIndex.value - 1 < 0) {
      return
    }
    selectedIndex.value = (selectedIndex.value - 1 + filteredEmojis.value.length) % filteredEmojis.value.length
    scrollToSelected('up')
  } else if (event.key === 'Enter' && selectedIndex.value >= 0) {
    event.preventDefault()
    selectEmoji()
  }
}

watch(() => props.show, (newValue) => {
  if (!newValue) {
    selectedIndex.value = -1
  }
})

watch(() => props.query, () => {
  selectedIndex.value = 0
})

onMounted(async () => {
  document.addEventListener('keydown', handleKeyDown)
  emojiData = await loadEmojiData()
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeyDown)
})

defineExpose({
  emojiDropdown,
  selectEmoji,
})
</script>

<style lang="scss" scoped>
.s-emojiDropdown {
  position: absolute;
  z-index: 10;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  width: 250px;
  bottom: 20px;
}

.s-emojiDropdown__item {
  border: none;
  background: none;
  border-radius: 4px;
  padding: 8px;
  cursor: pointer;
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;

  &:hover,
  &.selected {
    background-color: #f5f5f5;
  }

  .s-emojiDropdown__name {
    font-size: 14px;
    color: #666;
  }
}
</style>
